import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { KeycloakService, KeycloakAuthGuard } from 'keycloak-angular';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';

import { AccountRole, CurrentAccountRoles, RestrictedRoles } from 'models/account.model';
import { AccountService } from 'shared/services/account.service';
import { StoreService } from 'shared/services/store.service';

@Injectable({
  providedIn: 'root'
})
export class AppAuthGuard extends KeycloakAuthGuard {
  isDealer = false;

  accountRoles!: CurrentAccountRoles;

  restrictedRoles!: RestrictedRoles;

  constructor(
    protected readonly router: Router,
    protected readonly keycloak: KeycloakService,
    protected readonly storeService: StoreService,
    protected readonly accountService: AccountService
  ) {
    super(router, keycloak);
    this.init();
  }

  private init(): void {
    combineLatest([
      this.storeService.isCurrentAccountDealer(),
      this.storeService.getCurrentAccountRoles()
    ])
      .pipe(take(1))
      .subscribe(([isDealer, roles]: [boolean, CurrentAccountRoles]) => {
        this.isDealer = isDealer;
        this.accountRoles = roles;

        // we need to handle access in case the user switches a branch and the branch roles change
        this.handleUserRoles();
      });
  }

  private handleFirstTimeLogin(destinationRoute: string): void {
    this.storeService.hasAccountProfile()
      .pipe(take(1))
      .subscribe((hasAccountProfile: boolean): void => {
        if (destinationRoute === '/mein-profil/ersteinrichtung' && hasAccountProfile) {
          this.router.navigateByUrl('/');
        } else if (destinationRoute !== '/mein-profil/ersteinrichtung' && !hasAccountProfile) {
          this.router.navigateByUrl('mein-profil/ersteinrichtung');
        }
      });
  }

  private handleUserRoles(): void {
    if (!this.restrictedRoles) return;

    let activeRole: AccountRole;
    let accessDenied = false;

    if (this.isDealer && this.restrictedRoles.dealer) {
      activeRole = this.accountRoles.activeBranchRole as AccountRole;
      accessDenied = this.restrictedRoles.dealer.includes(activeRole);
    } else if (!this.isDealer && this.restrictedRoles.manufacturer) {
      activeRole = this.accountRoles.activeOrgRole as AccountRole;
      accessDenied = this.restrictedRoles.manufacturer.includes(activeRole);
    }

    if (accessDenied) {
      this.router.navigateByUrl('angebote');
    }
  }

  public async isAccessAllowed(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    // Force the user to log in if currently unauthenticated.
    if (!this.authenticated) {
      await this.keycloak.login({
        redirectUri: `${window.location.origin}${state.url}`
      });
    }

    this.handleFirstTimeLogin(state.url);

    // Handle Access for different Roles
    this.restrictedRoles = route.data.restrictedRoles;
    this.handleUserRoles();

    // Get the roles required from the route.
    const requiredRoles = route.data.roles;

    // Allow the user to to proceed if no additional roles are required to access the route.
    if (!(requiredRoles instanceof Array) || requiredRoles.length === 0) {
      return true;
    }

    // Allow the user to proceed if all the required roles are present.
    return requiredRoles.every((role) => this.roles.includes(role));
  }
}
